// HouseholdMergePicker — modal that lets the admin search for a
// surviving household to absorb a SOURCE household into.
//
// Two consumers:
//   1. QuarantineActionBar's "Merge into…" action — used to resolve
//      a migration-flagged duplicate by merging it into a real
//      surviving household.
//   2. (Wave 2.5) The household detail page's "Merge" button — gives
//      admins a discoverable, search-driven way to merge any two
//      households without going through a queue.
//
// The SOURCE household is always the merged_household_id (gets
// dissolved + FKs re-parented); the picked one is the primary
// (survives, absorbs the source's contacts / service records / etc.).
//
// Search hits GET /api/households?q=...&limit=20. Excludes the source
// household itself from the results so the user can't pick "merge
// into self."
//
// The merge call is POST /api/admin/households/:primary/merge with
// body { merged_household_id, reason }. Reason is required (audit log).

import { useEffect, useState } from "react";

import {
  api,
  ApiError,
  reconciliationApi,
  type Household,
  type HouseholdListItem,
  type MemberMergeOutcome,
  type MemberMergeRequest,
} from "../api";
import { Button } from "../ui";
import { HouseholdMergeReview } from "./HouseholdMergeReview";
import {
  buildContactNameMap,
  buildMemberOptions,
  describeFailedMemberMerges,
  type MergeMemberOption,
} from "./household-merge-review-helpers";
import { Modal } from "./Modal";
import { useToast } from "./Toast";

export interface HouseholdMergePickerProps {
  /**
   * The SOURCE household — gets ABSORBED into the picked household.
   * Its FKs re-parent and the row is hard-deleted by the merge
   * endpoint. (Naming kept as `quarantinedHouseholdId` for backward
   * compat with the QuarantineActionBar caller; the field is generic
   * "source household" semantically.)
   */
  quarantinedHouseholdId: string;
  quarantinedHouseholdName: string;
  /**
   * Optional default reason text to pre-fill the audit-log reason
   * input. Falls back to a generic string when not provided.
   */
  defaultReason?: string;
  /**
   * Pre-fills the search (Ask Stew's merge proposal hands the kept
   * household's name here). The person still picks a result, reviews
   * fields, gives a reason and confirms; nothing is pre-selected.
   */
  initialQuery?: string | undefined;
  onClose: () => void;
  /**
   * Called after a successful merge. Receives the surviving primary
   * household's id so the parent can navigate to that page (the
   * source household no longer exists).
   */
  onMerged: (primaryHouseholdId: string) => void;
}

const DEFAULT_REASON_FALLBACK = "Manual merge from household detail page";

export function HouseholdMergePicker({
  quarantinedHouseholdId,
  quarantinedHouseholdName,
  defaultReason,
  initialQuery,
  onClose,
  onMerged,
}: HouseholdMergePickerProps) {
  const toast = useToast();
  const [query, setQuery] = useState(initialQuery ?? "");
  const [results, setResults] = useState<HouseholdListItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [picked, setPicked] = useState<HouseholdListItem | null>(null);
  // Ryan's 2026-07-02 wrong-merge: the reason used to be PRE-FILLED,
  // which armed the merge button after a single ambiguous click on a
  // search result. The reason now starts EMPTY (the default is only a
  // placeholder) and an explicit confirm step stands between picking
  // and merging — two deliberate acts, not one accidental one.
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [merging, setMerging] = useState(false);
  // PR #305 — when the user clicks "Review fields", we fetch both
  // households' full row data and render HouseholdMergeReview as a
  // second-step modal. The review's onSubmit issues the actual API
  // call. Pre-review path is preserved when the user just clicks
  // "Merge" without reviewing — backward-compatible behavior.
  const [reviewing, setReviewing] = useState<{
    survivor: Household;
    merged: Household;
    // contact-id → name so the review's Primary contact row renders
    // names, not UUIDs (Baker, 2026-07-14).
    contactNameById: Record<string, string>;
    // Each household's people, so the review can offer to fold two cards
    // for one person together as part of the merge (Grant, 2026-09-17).
    membersByHouseholdId: Record<string, MergeMemberOption[]>;
  } | null>(null);
  const [loadingReview, setLoadingReview] = useState(false);

  // Debounced search. 250ms is enough to feel snappy without spamming
  // the endpoint on every keystroke.
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      setSearching(false);
      setSearchError(null);
      return;
    }
    setSearching(true);
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await api.listHouseholds({
          q: query.trim(),
          limit: 20,
        });
        if (cancelled) return;
        // Drop the quarantined household itself from the results — you
        // can't merge a household into itself.
        const filtered = res.households.filter(
          (h) => h.id !== quarantinedHouseholdId,
        );
        setResults(filtered);
        setSearchError(null);
      } catch (err) {
        if (cancelled) return;
        setSearchError(
          err instanceof ApiError
            ? `${err.status} — ${err.message}`
            : "Search failed",
        );
        setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, quarantinedHouseholdId]);

  async function handleMerge(
    fieldOverrides?: Record<string, "primary" | "merged">,
    memberMerges?: MemberMergeRequest[],
  ) {
    if (!picked || merging) return;
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      toast.error("Reason is required (captured in audit log)");
      return;
    }
    setMerging(true);
    try {
      const result = await reconciliationApi.mergeHouseholds(picked.id, {
        merged_household_id: quarantinedHouseholdId,
        reason: trimmed,
        ...(fieldOverrides && Object.keys(fieldOverrides).length > 0
          ? { field_overrides: fieldOverrides }
          : {}),
        ...(memberMerges && memberMerges.length > 0
          ? { member_merges: memberMerges }
          : {}),
      });
      if (!result.success) {
        toast.error("Merge failed");
        return;
      }
      reportMemberMergeOutcomes(result.member_merge_outcomes);
      onMerged(picked.id);
    } catch (err) {
      toast.error(
        err instanceof ApiError
          ? `${err.status} — ${err.message}`
          : "Merge failed",
      );
    } finally {
      setMerging(false);
    }
  }

  // The households merged either way, so a person who could NOT be folded
  // together has to be said out loud — otherwise the user leaves thinking
  // two cards became one.
  function reportMemberMergeOutcomes(
    outcomes: MemberMergeOutcome[] | undefined,
  ) {
    if (!outcomes || outcomes.length === 0) return;
    const folded = outcomes.filter((o) => o.merged).length;
    if (folded > 0) {
      toast.success(
        folded === 1
          ? "1 duplicate person was folded into a single record"
          : `${folded} duplicate people were folded into single records`,
      );
    }
    for (const line of describeFailedMemberMerges(
      outcomes,
      reviewing?.contactNameById ?? {},
    )) {
      toast.warning(line);
    }
  }

  // Open the per-field review modal. Fetches both households' full
  // rows (the search results only carry display + count metadata).
  async function openReview() {
    if (!picked || loadingReview) return;
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      toast.error("Reason is required (captured in audit log)");
      return;
    }
    setLoadingReview(true);
    try {
      const [survivorDetail, mergedDetail] = await Promise.all([
        api.getHousehold(picked.id),
        api.getHousehold(quarantinedHouseholdId),
      ]);
      setReviewing({
        survivor: survivorDetail.household,
        merged: mergedDetail.household,
        contactNameById: buildContactNameMap(
          survivorDetail.contacts,
          mergedDetail.contacts,
        ),
        membersByHouseholdId: {
          [picked.id]: buildMemberOptions(survivorDetail.contacts),
          [quarantinedHouseholdId]: buildMemberOptions(mergedDetail.contacts),
        },
      });
    } catch (err) {
      toast.error(
        err instanceof ApiError
          ? `${err.status} — ${err.message}`
          : "Could not load households for review",
      );
    } finally {
      setLoadingReview(false);
    }
  }

  if (reviewing) {
    return (
      <Modal
        open={true}
        title="Review household merge"
        onClose={() => (merging ? undefined : setReviewing(null))}
        className="modal-wide"
      >
        <HouseholdMergeReview
          survivor={reviewing.survivor}
          merged={reviewing.merged}
          contactNameById={reviewing.contactNameById}
          membersByHouseholdId={reviewing.membersByHouseholdId}
          initialReason={reason}
          onSubmit={async ({ field_overrides, reason: r, member_merges }) => {
            // The wizard owns the reason field too — sync it back so
            // a re-open of the picker shows the latest text.
            setReason(r);
            await handleMerge(
              field_overrides as Record<string, "primary" | "merged">,
              member_merges,
            );
          }}
          onCancel={() => setReviewing(null)}
        />
      </Modal>
    );
  }

  return (
    <Modal
      open={true}
      title={`Merge "${quarantinedHouseholdName}" into…`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={merging}>
            Cancel
          </Button>
          {picked && !confirming && (
            <Button
              variant="ghost"
              onClick={openReview}
              disabled={
                merging || loadingReview || reason.trim().length === 0
              }
            >
              {loadingReview ? "Loading…" : "Review fields"}
            </Button>
          )}
          {!confirming ? (
            <Button
              onClick={() => setConfirming(true)}
              disabled={!picked || merging || reason.trim().length === 0}
            >
              {picked
                ? reason.trim().length === 0
                  ? "Enter a reason first"
                  : "Continue…"
                : "Pick a destination first"}
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={() => setConfirming(false)}
                disabled={merging}
              >
                Back
              </Button>
              <Button onClick={() => handleMerge()} disabled={merging}>
                {merging
                  ? "Merging…"
                  : `Yes, merge into "${picked?.display_name ?? ""}"`}
              </Button>
            </>
          )}
        </>
      }
    >
      <p style={{ marginTop: 0, marginBottom: 12, fontSize: 13 }}>
        The destination household survives. <strong>"{quarantinedHouseholdName}"</strong>{" "}
        gets dissolved — its contacts, service records, opportunities,
        transactions, and rhythms re-parent to the destination.
        Audit-logged with the reason below. If you merge the wrong
        households, open the destination household → Actions →{" "}
        <strong>Undo a recent merge</strong>.
      </p>

      {/* The explicit confirm step — plain-English consequence before
          anything fires (the picker used to merge two clicks after
          opening, which is how Ryan merged the wrong Harrises). */}
      {confirming && picked && (
        <div
          style={{
            border: "1px solid var(--blue, #4e98c6)",
            background: "rgba(74, 152, 198, 0.08)",
            borderRadius: "var(--radius-compact)",
            padding: "10px 12px",
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          <strong>"{quarantinedHouseholdName}"</strong> will be dissolved
          into <strong>"{picked.display_name}"</strong>
          {picked.members_label ? ` (${picked.members_label})` : ""} — its
          contacts and records move there. You can undo this afterwards
          from "{picked.display_name}" → Actions → Undo a recent merge.
        </div>
      )}

      <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
        Search for the destination household
      </label>
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Type at least 2 characters…"
        style={{
          width: "100%",
          padding: "6px 8px",
          border: "1px solid var(--mid-gray)",
          borderRadius: "var(--radius-compact)",
          fontSize: 14,
        }}
        autoFocus
      />

      {searching && (
        <p
          className="muted"
          style={{ fontSize: 12, margin: "6px 0 0" }}
        >
          Searching…
        </p>
      )}
      {searchError && (
        <p style={{ color: "var(--danger)", fontSize: 12, margin: "6px 0 0" }}>
          {searchError}
        </p>
      )}
      {!searching && query.trim().length >= 2 && results.length === 0 && (
        <p
          className="muted"
          style={{ fontSize: 12, margin: "6px 0 0" }}
        >
          No matching households.
        </p>
      )}

      {results.length > 0 && (
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: "8px 0 0",
            maxHeight: 240,
            overflowY: "auto",
            border: "1px solid var(--mid-gray)",
            borderRadius: "var(--radius-compact)",
          }}
        >
          {results.map((h) => {
            const isPicked = picked?.id === h.id;
            return (
              <li key={h.id}>
                <button
                  type="button"
                  onClick={() => {
                    setPicked(h);
                    // Changing the pick always drops back out of the
                    // confirm step — never confirm against a stale pick.
                    setConfirming(false);
                  }}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    background: isPicked
                      ? "rgba(74, 152, 198, 0.12)"
                      : "transparent",
                    border: "none",
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: 13,
                    borderBottom: "1px solid var(--mid-gray)",
                  }}
                >
                  <div
                    style={{ fontWeight: 600, color: "var(--navy)" }}
                  >
                    {h.display_name}
                  </div>
                  {/* WHO is in this household — so two "Harris
                      Household" results are tellable apart (the
                      wrong-merge trap). */}
                  {h.members_label && (
                    <div className="muted" style={{ fontSize: 11.5 }}>
                      {h.members_label}
                    </div>
                  )}
                  <div className="muted" style={{ fontSize: 11 }}>
                    {h.contact_count}{" "}
                    {h.contact_count === 1 ? "contact" : "contacts"}
                    {h.primary_city &&
                      h.primary_state &&
                      ` · ${h.primary_city}, ${h.primary_state}`}
                    {h.company_slugs.length > 0 &&
                      ` · ${h.company_slugs.join(", ")}`}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <label
        style={{
          display: "block",
          fontSize: 13,
          marginTop: 16,
          marginBottom: 4,
        }}
      >
        Reason (captured in audit log, required)
      </label>
      <input
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={defaultReason ?? DEFAULT_REASON_FALLBACK}
        style={{
          width: "100%",
          padding: "6px 8px",
          border: "1px solid var(--mid-gray)",
          borderRadius: "var(--radius-compact)",
          fontSize: 14,
        }}
      />
    </Modal>
  );
}
